import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchDesigns,
  createDesign,
  updateDesign,
  deleteDesign,
  toggleDesignStock,
} from "../api/DesignAPIs";
import {
  fetchCategories,
  fetchCategoryById,
  fetchDesignsForCategory,
} from "../api/CategoryAPIs";
import { colors } from "../constants/colors";
import { API_BASE_URL_V2 } from "../api/URL_Header";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronRight,
  Package,
  Edit,
  Trash2,
  Plus,
  Save,
  X,
  Image as ImageIcon,
  Layers,
  Eye,
  Grid,
  Tag,
  DollarSign,
  TrendingUp,
  AlertCircle,
  Link as LinkIcon,
  Download,
  Loader,
  Filter,
  Search,
  ChevronLeft,
  ChevronUp,
  CheckCircle,
  XCircle,
} from "lucide-react";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(5)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-20 h-20 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-3">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const DesignsPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [designs, setDesigns] = useState([]);
  const [newDesign, setNewDesign] = useState(null);
  const [editingDesign, setEditingDesign] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [togglingStockId, setTogglingStockId] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [totalDesigns, setTotalDesigns] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Fetch categories (for dropdown)
  const {
    data: categoryData,
    isLoading: isLoadingCategories,
    error: errorCategories,
  } = useQuery({
    queryKey: categoryId ? ["category", categoryId] : ["categories"],
    queryFn: categoryId ? () => fetchCategoryById(categoryId) : fetchCategories,
    staleTime: Infinity,
  });

  // Fetch designs with pagination
  const {
    data: designData,
    isLoading: isLoadingDesigns,
    isFetching,
    error: errorDesigns,
  } = useQuery({
    queryKey: ["designs", categoryId || "all", currentPage, pageSize],
    queryFn: categoryId
      ? () => fetchDesignsForCategory(categoryId, currentPage, pageSize)
      : () => fetchDesigns(currentPage, pageSize),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 5,
  });

  const isLoading = isLoadingCategories || isLoadingDesigns;
  const error = errorCategories || errorDesigns;

  // Load More function
  const loadMoreDesigns = () => {
    if (!isLoadingMore && hasMore) {
      setIsLoadingMore(true);
      setCurrentPage((prev) => prev + 1);
    }
  };

  // Update designs when new data arrives
  useEffect(() => {
    if (designData && categoryData) {
      const newDesigns = (designData.designs || []).map((design) => ({
        id: design.designId,
        name: design.name || "Unnamed Product",
        imageUrl_1: design.imageUrl_1 || "",
        imageUrl_2: design.imageUrl_2 || "",
        newPrice: design.priceAfter || 0,
        oldPrice: design.priceBefore || 0,
        inStock: design.inStock !== undefined ? design.inStock : true,
        hasOffer: design.hasOffer || false,
        category: categoryId
          ? categoryData?.name?.trim() || "Uncategorized"
          : Array.isArray(categoryData)
          ? categoryData.find((c) => c.id === design.categoryId)?.name?.trim() || "Uncategorized"
          : "Uncategorized",
        categoryId: design.categoryId || categoryId || "",
        driveLink: design.driveLink || "",
        code: design.code || "",
        sales: design.sales || 0,
      }));

      if (currentPage === 1) {
        setDesigns(newDesigns.sort((a, b) => b.sales - a.sales));
      } else {
        setDesigns((prev) => [...prev, ...newDesigns].sort((a, b) => b.sales - a.sales));
      }

      setTotalDesigns(designData.totalCount || 0);
      setHasMore(currentPage * pageSize < (designData.totalCount || 0));
      setIsLoadingMore(false);
    }
  }, [designData, categoryData, categoryId, currentPage]);

  const handleImageChange = (e, field, setState, state) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setState({
          ...state,
          [field]: reader.result.split(",")[1],
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e, setState, state) => {
    const { name, value, type, checked } = e.target;
    setState({
      ...state,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    setCreateError(null);
    try {
      const created = await createDesign({
        ...newDesign,
        newPrice: newDesign.newPrice || 180,
        oldPrice: newDesign.oldPrice || 200,
        code: newDesign.code || "None",
      });
      await queryClient.invalidateQueries({
        queryKey: ["designs", categoryId || "all"],
      });
      setNewDesign(null);
      toast.success("تم إضافة التصميم بنجاح!");
    } catch (err) {
      console.error("Error creating design:", err);
      setCreateError(err.message || "فشل في إضافة التصميم.");
      toast.error("فشل في إضافة التصميم!");
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateDesign(editingDesign.id, editingDesign);
      await queryClient.invalidateQueries({
        queryKey: ["designs", categoryId || "all"],
      });
      setEditingDesign(null);
      toast.success("تم تحديث التصميم بنجاح!");
    } catch (err) {
      console.error("Error updating design:", err);
      toast.error("فشل في تحديث التصميم!");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("هل أنت متأكد أنك تريد حذف هذا التصميم؟")) {
      try {
        await deleteDesign(id);
        setDesigns((prev) => prev.filter((d) => d.id !== id));
        toast.success("تم حذف التصميم بنجاح!");
      } catch (err) {
        toast.error("فشل في حذف التصميم!");
      }
    }
  };

  const handleToggleStock = async (design) => {
    setTogglingStockId(design.id);
    try {
      const result = await toggleDesignStock(design.id);
      setDesigns((prev) =>
        prev.map((d) =>
          d.id === design.id ? { ...d, inStock: result.inStock } : d
        )
      );
      toast.success(
        result.inStock
          ? "تم تفعيل التصميم — متاح للشراء"
          : "تم إيقاف التصميم — غير متوفر للشراء"
      );
    } catch (err) {
      console.error("Error toggling design stock:", err);
      toast.error("فشل في تغيير حالة التصميم!");
    } finally {
      setTogglingStockId(null);
    }
  };

  // Filter designs based on search term
  const filteredDesigns = designs.filter(design => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      design.name.toLowerCase().includes(term) ||
      design.code.toLowerCase().includes(term) ||
      design.id.toString().includes(term)
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Grid className="text-indigo-600" size={32} />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              إدارة التصميمات
              {categoryId && categoryData?.name && (
                <span className="text-2xl mr-3 text-gray-600">- {categoryData.name}</span>
              )}
            </h1>
          </div>
          <button
            onClick={() => navigate("/categories")}
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
          >
            <ChevronRight size={20} />
            العودة للفئات
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl mb-6 flex items-center gap-3">
            <AlertCircle className="text-red-500" size={24} />
            <p>فشل في تحميل البيانات. حاول مرة أخرى.</p>
          </div>
        )}

        {/* Stats and Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Stats Card */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-indigo-100 rounded-xl">
                  <Layers className="text-indigo-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">عدد التصميمات</h2>
                  <p className="text-sm text-gray-600">إجمالي التصميمات المتاحة</p>
                </div>
              </div>
              <div className="text-4xl font-bold text-indigo-600">{designs.length}</div>
            </div>
          </div>

          {/* Add Button */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 flex items-center justify-end">
            <button
              onClick={() =>
                setNewDesign({
                  categoryId: categoryId || "",
                  name: "",
                  imageBase64_1: "",
                  imageBase64_2: "",
                  oldPrice: 200,
                  newPrice: 180,
                  driveLink: "",
                  inStock: true,
                  hasOffer: false,
                  code: "",
                })
              }
              className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 py-4 rounded-xl shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center gap-2 font-bold text-lg"
            >
              <Plus size={24} />
              إضافة تصميم جديد
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="بحث برقم التصميم أو الاسم أو الكود..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-400 transition-all"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
            >
              <Filter size={20} />
              تصفية
            </button>
          </div>
        </div>

        {/* Designs Table */}
        {isLoading && currentPage === 1 ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6">
            <SkeletonLoader type="table" />
          </div>
        ) : filteredDesigns.length > 0 ? (
          <>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 overflow-x-auto">
              <table className="w-full min-w-[1000px] table-auto">
                <thead>
                  <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b-2 border-indigo-200">
                    <th className="p-4 text-center font-bold text-indigo-800">#ID</th>
                    <th className="p-4 text-center font-bold text-indigo-800">الصورة</th>
                    <th className="p-4 text-right font-bold text-indigo-800">الاسم</th>
                    <th className="p-4 text-center font-bold text-indigo-800">الكود</th>
                    <th className="p-4 text-center font-bold text-indigo-800">المبيعات</th>
                    <th className="p-4 text-center font-bold text-indigo-800">السعر</th>
                    <th className="p-4 text-center font-bold text-indigo-800">الحالة</th>
                    <th className="p-4 text-center font-bold text-indigo-800">الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDesigns.map((design) => (
                    <tr
                      key={design.id}
                      className={`border-b border-gray-200 hover:bg-white/60 transition-colors ${
                        design.inStock ? "" : "bg-red-50/30"
                      }`}
                    >
                      <td className="p-4 text-center">
                        <span className="font-mono font-bold text-indigo-600">#{design.id}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex justify-center">
                          {design.imageUrl_1 ? (
                            <img
                              src={`${API_BASE_URL_V2}${design.imageUrl_1}`}
                              alt={design.name}
                              className="h-22 w-20 object-cover rounded-xl border-2 border-gray-200 shadow-sm"
                              loading="lazy"
                            />
                          ) : (
                            <div className="h-20 w-20 bg-gray-100 rounded-xl flex items-center justify-center border-2 border-dashed border-gray-300">
                              <ImageIcon className="text-gray-400" size={24} />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="text-xl p-2 font-medium">{design.name}</td>
                      <td className="p-4 text-center">
                        <span className="bg-gray-100 px-3 py-1 rounded-lg font-mono text-sm">
                          {design.code}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <TrendingUp size={16} className="text-green-600" />
                          <span className="text-2xl font-bold text-green-600">{design.sales}</span>
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex flex-col items-center">
                          {design.oldPrice > design.newPrice && (
                            <span className="text-xl line-through text-gray-400 text-sm">
                              {design.oldPrice}
                            </span>
                          )}
                          <span className="text-2xl font-bold text-green-600">
                            {design.newPrice} ج
                          </span>
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <button
                            onClick={() => handleToggleStock(design)}
                            disabled={togglingStockId === design.id}
                            title={
                              design.inStock
                                ? "اضغط لجعل التصميم غير متوفر"
                                : "اضغط لتفعيل التصميم"
                            }
                            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all duration-300 flex items-center justify-center gap-2 min-w-[120px] disabled:opacity-60 disabled:cursor-wait ${
                              design.inStock
                                ? "bg-green-100 text-green-700 border border-green-200 hover:bg-green-200"
                                : "bg-red-100 text-red-700 border border-red-200 hover:bg-red-200"
                            }`}
                          >
                            {togglingStockId === design.id ? (
                              <Loader className="animate-spin" size={16} />
                            ) : design.inStock ? (
                              <>
                                <CheckCircle size={16} />
                                متوفر
                              </>
                            ) : (
                              <>
                                <XCircle size={16} />
                                غير متوفر
                              </>
                            )}
                          </button>
                          {design.hasOffer && (
                            <span className="text-orange-600 text-xs bg-orange-100 px-2 py-1 rounded-full">
                              عرض خاص
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2 justify-center">
                          <button
                            onClick={() => setEditingDesign(design)}
                            className="bg-blue-500 text-white px-3 py-2 rounded-lg hover:bg-blue-600 hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center gap-1"
                          >
                            <Edit size={14} />
                            تعديل
                          </button>
                          <button
                            onClick={() => handleDelete(design.id)}
                            className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center gap-1"
                          >
                            <Trash2 size={14} />
                            حذف
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Load More Button */}
            <div className="text-center mt-8">
              {hasMore && (
                <button
                  onClick={loadMoreDesigns}
                  disabled={isFetching}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-10 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                >
                  {isFetching ? (
                    <>
                      <Loader className="animate-spin" size={20} />
                      جاري التحميل...
                    </>
                  ) : (
                    <>
                      <ChevronLeft size={20} />
                      تحميل المزيد
                    </>
                  )}
                </button>
              )}

              {!hasMore && totalDesigns > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 inline-block">
                  <p className="text-green-700 font-bold text-lg">
                    ✓ تم تحميل جميع التصميمات ({totalDesigns})
                  </p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-16 text-center">
            <Package className="mx-auto mb-4 text-gray-400" size={64} />
            <p className="text-2xl text-gray-700 mb-2">لا توجد تصميمات</p>
            <p className="text-lg text-gray-500">قم بإضافة تصميم جديد للبدء</p>
          </div>
        )}

        {/* Create Design Modal */}
        {newDesign && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-r from-green-600 to-emerald-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
                <h2 className="text-2xl font-bold">إضافة تصميم جديد</h2>
                <button
                  onClick={() => setNewDesign(null)}
                  className="hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>

              {createError && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-4 m-6 rounded-xl flex items-center gap-3">
                  <AlertCircle className="text-red-500" size={20} />
                  <p>{createError}</p>
                </div>
              )}

              <form onSubmit={handleCreate} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Category */}
                  <div className="space-y-2">
                    <label className="block text-right font-medium text-gray-700">الفئة</label>
                    <select
                      name="categoryId"
                      value={newDesign.categoryId}
                      onChange={(e) => handleInputChange(e, setNewDesign, newDesign)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-300 focus:border-green-400 transition-all"
                      required
                    >
                      <option value="">اختر الفئة</option>
                      {(Array.isArray(categoryData) ? categoryData : [categoryData]).map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Name */}
                  <div className="space-y-2">
                    <label className="block text-right font-medium text-gray-700">اسم التصميم</label>
                    <input
                      type="text"
                      name="name"
                      value={newDesign.name}
                      onChange={(e) => handleInputChange(e, setNewDesign, newDesign)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-300 focus:border-green-400 transition-all"
                      placeholder="أدخل اسم التصميم"
                      required
                    />
                  </div>

                  {/* Code */}
                  <div className="space-y-2">
                    <label className="block text-right font-medium text-gray-700">الكود</label>
                    <input
                      type="text"
                      name="code"
                      value={newDesign.code}
                      onChange={(e) => handleInputChange(e, setNewDesign, newDesign)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-300 focus:border-green-400 transition-all"
                      placeholder="أدخل الكود (مثل: G001)"
                    />
                  </div>

                  {/* Drive Link */}
                  <div className="space-y-2">
                    <label className="block text-right font-medium text-gray-700">رابط درايف</label>
                    <input
                      type="text"
                      name="driveLink"
                      value={newDesign.driveLink}
                      onChange={(e) => handleInputChange(e, setNewDesign, newDesign)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-300 focus:border-green-400 transition-all"
                      placeholder="أدخل رابط الدرايف"
                    />
                  </div>

                  {/* Old Price */}
                  <div className="space-y-2">
                    <label className="block text-right font-medium text-gray-700">السعر قبل الخصم</label>
                    <input
                      type="number"
                      name="oldPrice"
                      value={newDesign.oldPrice}
                      onChange={(e) => handleInputChange(e, setNewDesign, newDesign)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-300 focus:border-green-400 transition-all"
                    />
                  </div>

                  {/* New Price */}
                  <div className="space-y-2">
                    <label className="block text-right font-medium text-gray-700">السعر بعد الخصم</label>
                    <input
                      type="number"
                      name="newPrice"
                      value={newDesign.newPrice}
                      onChange={(e) => handleInputChange(e, setNewDesign, newDesign)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-300 focus:border-green-400 transition-all"
                    />
                  </div>

                  {/* Image 1 */}
                  <div className="space-y-2 md:col-span-2">
                    <label className="block text-right font-medium text-gray-700">الصورة 1</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-green-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, "imageBase64_1", setNewDesign, newDesign)}
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Image 2 */}
                  <div className="space-y-2 md:col-span-2">
                    <label className="block text-right font-medium text-gray-700">الصورة 2 (اختياري)</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-green-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, "imageBase64_2", setNewDesign, newDesign)}
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Checkboxes */}
                  <div className="flex items-center gap-8">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="inStock"
                        checked={newDesign.inStock}
                        onChange={(e) => handleInputChange(e, setNewDesign, newDesign)}
                        className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                      />
                      <span className="font-medium text-gray-700">في المخزون</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="hasOffer"
                        checked={newDesign.hasOffer}
                        onChange={(e) => handleInputChange(e, setNewDesign, newDesign)}
                        className="w-5 h-5 text-orange-600 rounded focus:ring-orange-500"
                      />
                      <span className="font-medium text-gray-700">عرض خاص</span>
                    </label>
                  </div>
                </div>

                {/* Form Buttons */}
                <div className="flex justify-center gap-4 mt-8">
                  <button
                    type="button"
                    onClick={() => setNewDesign(null)}
                    className="flex items-center gap-2 bg-gray-400 text-white px-8 py-3 rounded-xl hover:bg-gray-500 font-bold text-lg transition"
                  >
                    <X size={20} />
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    disabled={isCreating}
                    className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-10 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg disabled:opacity-70"
                  >
                    {isCreating ? (
                      <>
                        <Loader className="animate-spin" size={20} />
                        جاري الإضافة...
                      </>
                    ) : (
                      <>
                        <Save size={20} />
                        إضافة
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Design Modal */}
        {editingDesign && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
                <h2 className="text-2xl font-bold">تعديل التصميم #{editingDesign.id}</h2>
                <button
                  onClick={() => setEditingDesign(null)}
                  className="hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleUpdate} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Category */}
                  <div className="space-y-2">
                    <label className="block text-right font-medium text-gray-700">الفئة</label>
                    <select
                      name="categoryId"
                      value={editingDesign.categoryId}
                      onChange={(e) => handleInputChange(e, setEditingDesign, editingDesign)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-300 focus:border-blue-400 transition-all"
                      required
                    >
                      <option value="">اختر الفئة</option>
                      {(Array.isArray(categoryData) ? categoryData : [categoryData]).map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Name */}
                  <div className="space-y-2">
                    <label className="block text-right font-medium text-gray-700">اسم التصميم</label>
                    <input
                      type="text"
                      name="name"
                      value={editingDesign.name}
                      onChange={(e) => handleInputChange(e, setEditingDesign, editingDesign)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-300 focus:border-blue-400 transition-all"
                      required
                    />
                  </div>

                  {/* Code (Read Only) */}
                  <div className="space-y-2">
                    <label className="block text-right font-medium text-gray-700">الكود</label>
                    <input
                      type="text"
                      value={editingDesign.code}
                      readOnly
                      className="w-full p-3 border-2 border-gray-200 rounded-xl bg-gray-100 cursor-not-allowed"
                    />
                  </div>

                  {/* Drive Link */}
                  <div className="space-y-2">
                    <label className="block text-right font-medium text-gray-700">رابط درايف</label>
                    <input
                      type="text"
                      name="driveLink"
                      value={editingDesign.driveLink}
                      onChange={(e) => handleInputChange(e, setEditingDesign, editingDesign)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-300 focus:border-blue-400 transition-all"
                    />
                  </div>

                  {/* Old Price */}
                  <div className="space-y-2">
                    <label className="block text-right font-medium text-gray-700">السعر قبل الخصم</label>
                    <input
                      type="number"
                      name="oldPrice"
                      value={editingDesign.oldPrice}
                      onChange={(e) => handleInputChange(e, setEditingDesign, editingDesign)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-300 focus:border-blue-400 transition-all"
                    />
                  </div>

                  {/* New Price */}
                  <div className="space-y-2">
                    <label className="block text-right font-medium text-gray-700">السعر بعد الخصم</label>
                    <input
                      type="number"
                      name="newPrice"
                      value={editingDesign.newPrice}
                      onChange={(e) => handleInputChange(e, setEditingDesign, editingDesign)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-300 focus:border-blue-400 transition-all"
                    />
                  </div>

                  {/* Current Images */}
                  {editingDesign.imageUrl_1 && (
                    <div className="space-y-2">
                      <label className="block text-right font-medium text-gray-700">الصورة 1 الحالية</label>
                      <img
                        src={`${API_BASE_URL_V2}${editingDesign.imageUrl_1}`}
                        alt={editingDesign.name}
                        className="h-32 w-32 object-cover rounded-xl border-2 border-gray-200"
                      />
                    </div>
                  )}

                  {editingDesign.imageUrl_2 && (
                    <div className="space-y-2">
                      <label className="block text-right font-medium text-gray-700">الصورة 2 الحالية</label>
                      <img
                        src={`${API_BASE_URL_V2}${editingDesign.imageUrl_2}`}
                        alt={editingDesign.name}
                        className="h-32 w-32 object-cover rounded-xl border-2 border-gray-200"
                      />
                    </div>
                  )}

                  {/* New Images */}
                  <div className="space-y-2 md:col-span-2">
                    <label className="block text-right font-medium text-gray-700">صورة 1 جديدة (اختياري)</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, "imageBase64_1", setEditingDesign, editingDesign)}
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <label className="block text-right font-medium text-gray-700">صورة 2 جديدة (اختياري)</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, "imageBase64_2", setEditingDesign, editingDesign)}
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Checkboxes */}
                  <div className="flex items-center gap-8">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="inStock"
                        checked={editingDesign.inStock}
                        onChange={(e) => handleInputChange(e, setEditingDesign, editingDesign)}
                        className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                      />
                      <span className="font-medium text-gray-700">في المخزون</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="hasOffer"
                        checked={editingDesign.hasOffer}
                        onChange={(e) => handleInputChange(e, setEditingDesign, editingDesign)}
                        className="w-5 h-5 text-orange-600 rounded focus:ring-orange-500"
                      />
                      <span className="font-medium text-gray-700">عرض خاص</span>
                    </label>
                  </div>
                </div>

                {/* Form Buttons */}
                <div className="flex justify-center gap-4 mt-8">
                  <button
                    type="button"
                    onClick={() => setEditingDesign(null)}
                    className="flex items-center gap-2 bg-gray-400 text-white px-8 py-3 rounded-xl hover:bg-gray-500 font-bold text-lg transition"
                  >
                    <X size={20} />
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-10 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg"
                  >
                    <Save size={20} />
                    حفظ التغييرات
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      {/* Floating Button to Scroll to Top - أضف هذا الزر الجديد */}
      <div className="fixed bottom-6 right-6">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-4 rounded-full shadow-2xl hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center"
          title="العودة إلى الأعلى"
        >
          <ChevronUp size={24} />
        </button>
      </div>
    </div>
  );
};

export default DesignsPage;